import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  deleteDoc,
  doc,
  writeBatch,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import {
  FaSpinner,
  FaTrash,
  FaCheckCircle,
  FaSortAlphaDown,
  FaSortAlphaUp,
  FaCalendarAlt,
  FaSortAmountDown,
  FaSortAmountUp,
  FaChartLine,
  FaMapMarkerAlt,
  FaExclamationTriangle,
  FaInfoCircle,
  FaSearch,
  FaBell,
  FaCloudSun,
  FaWind,
  FaTint,
  FaTimes,
  FaCheck,
  FaTemperatureHigh,
  FaLeaf,
  FaBug,
  FaTachometerAlt,
  FaCalendarDay,
  FaClock,
} from "react-icons/fa";
import PropTypes from "prop-types";

import { useNavigate } from "react-router-dom";
import { compareFields, fetchResults } from "./Refresh";
import Swal from "sweetalert2";
import { urls } from "../../services/apiService";
import { useTranslation } from "react-i18next";

// Function to format date to mm/dd/yyyy
const formatDate = (date) => {
  const options = { year: "numeric", month: "2-digit", day: "2-digit" };
  return new Intl.DateTimeFormat("en-US", options).format(date);
};

// Helper function to get last weather API call timestamp
const getLastWeatherCallTime = () => {
  const lastCall = localStorage.getItem("lastWeatherProcessCall");
  return lastCall ? new Date(lastCall) : null;
};

// Helper function to save last weather API call timestamp
const saveLastWeatherCallTime = () => {
  localStorage.setItem("lastWeatherProcessCall", new Date().toISOString());
};

// Helper function to get next scheduled call time
const getNextScheduledCallTime = () => {
  const lastCall = getLastWeatherCallTime();
  if (!lastCall) return null;
  const nextCall = new Date(lastCall);
  nextCall.setHours(nextCall.getHours() + 36);
  return nextCall;
};

// Helper function to check if 1.5 days (36 hours) have passed
const shouldCallWeatherAPI = () => {
  const lastCall = getLastWeatherCallTime();
  if (!lastCall) return true;
  
  const hoursSinceLastCall = (new Date() - lastCall) / (1000 * 60 * 60);
  return hoursSinceLastCall >= 36;
};

// Function to call weather/process API
const callWeatherProcessAPI = async (username) => {
  try {
    const response = await fetch(urls.weather_process, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username }),
    });
    
    if (!response.ok) {
      throw new Error(`Weather API failed: ${response.status}`);
    }
    
    const data = await response.json();
    console.log("Weather process completed:", data);
    saveLastWeatherCallTime();
    return data;
  } catch (error) {
    console.error("Error calling weather process API:", error);
    throw error;
  }
};

// Function to fetch weather notifications
const fetchWeatherNotifications = async (username) => {
  try {
    const response = await fetch(urls.weather_notifications, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username }),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch notifications: ${response.status}`);
    }
    
    const data = await response.json();
    return data.notifications || [];
  } catch (error) {
    console.error("Error fetching weather notifications:", error);
    return [];
  }
};

// Function to mark a single notification as read
const markNotificationAsRead = async (notificationId) => {
  try {
    const response = await fetch(urls.weather_notifications_read, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: notificationId }),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to mark notification as read: ${response.status}`);
    }
    
    return true;
  } catch (error) {
    console.error("Error marking notification as read:", error);
    return false;
  }
};

// Function to mark all notifications as read
const markAllNotificationsAsRead = async (username) => {
  try {
    const response = await fetch(urls.weather_notifications_read_all, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username }),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to mark all notifications as read: ${response.status}`);
    }
    
    const data = await response.json();
    console.warn("All notifications marked as read:", data);
    return data;
  } catch (error) {
    console.error("Error marking all notifications as read:", error);
    return false;
  }
};

const FieldData = () => {
  const [error, setError] = useState("");
  const [loadingFields, setLoadingFields] = useState({});
  const [completedFields, setCompletedFields] = useState({});
  const [fieldExistence, setFieldExistence] = useState({});
  const [lastFetchedDates, setLastFetchedDates] = useState({});
  const [result, setResults] = useState([]);
  const [sortBy, setSortBy] = useState("alphabetical");
  const [sortOrder, setSortOrder] = useState("asc");
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedFields, setSelectedFields] = useState([]);
  const [limitField, setLimitField] = useState(null);
  const [dateLoadingStates, setDateLoadingStates] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [textMode, setTextMode] = useState("full");
  const [isActive, setIsActive] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [markingAsRead, setMarkingAsRead] = useState(false);
  const [timeUntilNextUpdate, setTimeUntilNextUpdate] = useState(null);
  const [fieldsLoaded, setFieldsLoaded] = useState(false); // Add state to track if fields are loaded
  const [weatherInitialized, setWeatherInitialized] = useState(false); // Add state to track if weather has been initialized

  const buttonContainerRef = useRef(null);
  const notificationPanelRef = useRef(null);
  const bellButtonRef = useRef(null);
  const timerIntervalRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationPanelRef.current && 
          !notificationPanelRef.current.contains(event.target) &&
          bellButtonRef.current &&
          !bellButtonRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Function to get last date from backend
  const getLastDateFromBackend = async (fieldName) => {
    try {
      const lastDate = await fetchResults(fieldName);
      return lastDate;
    } catch (error) {
      console.error("Error fetching last date from backend:", error);
      return null;
    }
  };

  // Function to save last date to Firebase
  const saveLastDateToFirebase = async (fieldId, fieldName, date) => {
    try {
      const user = getAuth().currentUser;
      if (!user) return;

      const db = getFirestore();
      const fieldRef = doc(db, `users/${user.uid}/fields`, fieldId);

      await updateDoc(fieldRef, {
        lastFetchedDate: date || "No data available",
        lastUpdated: Timestamp.now(),
      });

      console.log(`✅ Last date saved to Firebase for ${fieldName}:`, date || "No data available");
      return date;
    } catch (error) {
      console.error("Error saving last date to Firebase:", error);
      return null;
    }
  };

  // Function to fetch and save last date for fields without it
  const fetchAndSaveLastDate = async (field) => {
    const { id: fieldId, fieldName } = field;

    try {
      const backendDate = await getLastDateFromBackend(fieldName);
      const dateToSave = backendDate || "No data available";

      await saveLastDateToFirebase(fieldId, fieldName, dateToSave);

      return backendDate;
    } catch (error) {
      console.error(`Error fetching last date for ${fieldName}:`, error);
      await saveLastDateToFirebase(fieldId, fieldName, "No data available");
      return null;
    }
  };

  // Process fields that don't have lastFetchedDate
  useEffect(() => {
    const processMissingLastDates = async () => {
      if (!result.length) return;

      const dates = { ...lastFetchedDates };
      const loadingStates = { ...dateLoadingStates };
      let needsUpdate = false;

      const fieldsToProcess = result.filter(
        (field) =>
          !field.lastFetchedDate ||
          field.lastFetchedDate === "No data available",
      );

      fieldsToProcess.forEach((field) => {
        loadingStates[field.fieldName] = true;
        needsUpdate = true;
      });

      if (needsUpdate) {
        setDateLoadingStates(loadingStates);
      }

      for (const field of fieldsToProcess) {
        try {
          const lastFetchedDate = await fetchAndSaveLastDate(field);
          dates[field.fieldName] = formatLastFetchedDate(lastFetchedDate);
        } catch (error) {
          console.error(`Error processing last date for ${field.fieldName}:`, error);
          dates[field.fieldName] = "No data available";
        } finally {
          loadingStates[field.fieldName] = false;
        }
      }

      if (needsUpdate) {
        setLastFetchedDates(dates);
        setDateLoadingStates(loadingStates);
      }
    };

    processMissingLastDates();
  }, [result]);
  
  const loadWeatherData = useCallback(async () => {
    const user = getAuth().currentUser;
    if (!user) return;

    // Only proceed if fields are loaded and there are fields
    if (!fieldsLoaded || result.length === 0) {
      console.log("Skipping weather data load: No fields available or fields not loaded yet");
      return;
    }

    try {
      // Check if we need to call weather/process API
      if (shouldCallWeatherAPI()) {
        setWeatherLoading(true);
        console.log("Calling weather process API for user:", user.email);
        
        await callWeatherProcessAPI(user.email);
        
        // Show success notification for weather update
        Swal.fire({
          title: "Weather Data Updated",
          text: "Latest weather information and alerts have been loaded",
          icon: "success",
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      }
      
      // Fetch weather notifications
      await loadNotifications(user.email);
      
    } catch (error) {
      console.error("Error loading weather data:", error);
    } finally {
      setWeatherLoading(false);
    }
  }, [fieldsLoaded, result.length]);
  
  const updateCountdownTimer = useCallback(() => {
    const lastCall = getLastWeatherCallTime();
    if (!lastCall) {
      setTimeUntilNextUpdate(null);
      return;
    }
    
    const nextCall = new Date(lastCall);
    nextCall.setHours(nextCall.getHours() + 36);
    const now = new Date();
    const diff = nextCall - now;
    
    if (diff <= 0) {
      setTimeUntilNextUpdate(null);
      // Trigger weather update if timer reaches zero AND fields are loaded
      if (shouldCallWeatherAPI() && fieldsLoaded && result.length > 0) {
        loadWeatherData();
      }
    } else {
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setTimeUntilNextUpdate({ hours, minutes, seconds });
    }
  }, [fieldsLoaded, result.length, loadWeatherData]);

  const loadNotifications = async (email) => {
    try {
      const weatherNotifs = await fetchWeatherNotifications(email);
      
      // Format notifications
      const formattedNotifications = weatherNotifs.map(notif => ({
        id: notif.id,
        title: notif.title || getNotificationTitle(notif),
        message: notif.message || getNotificationMessage(notif),
        read: notif.is_read === 1 || notif.is_read === true,
        time: formatNotificationTime(notif.created_at),
        alert_date: notif.alert_date,
        alert_type: notif.alert_type,
        severity: notif.severity,
        source: notif.source,
        fieldname: notif.fieldname,
        created_at: notif.created_at,
        // Additional details for the detailed popup
        temperature: notif.temperature,
        humidity: notif.humidity,
        wind_speed: notif.wind_speed,
        rainfall: notif.rainfall,
        recommendations: getRecommendations(notif.alert_type, notif.severity)
      }));
      
      // Sort by date (newest first)
      formattedNotifications.sort((a, b) => {
        const dateA = a.alert_date || a.created_at;
        const dateB = b.alert_date || b.created_at;
        return new Date(dateB) - new Date(dateA);
      });
      
      setNotifications(formattedNotifications);
      
      // Update unread count
      const unreadNotifs = formattedNotifications.filter(n => !n.read);
      setUnreadCount(unreadNotifs.length);
      
      // Show toast for new unread notifications (only if there are new ones)
      const previousUnreadCount = unreadCount;
      if (unreadNotifs.length > 0 && unreadNotifs.length !== previousUnreadCount) {
        const latestUnread = unreadNotifs[0];
        Swal.fire({
          title: latestUnread.title,
          text: latestUnread.message,
          icon: getSeverityIcon(latestUnread.severity),
          timer: 5000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      }
    } catch (error) {
      console.error("Error loading notifications:", error);
    }
  };

  // Get recommendations based on alert type and severity
  const getRecommendations = (alertType, severity) => {
    const recommendations = {
      bacterial_blight: "Apply copper-based fungicides. Remove infected plant debris. Ensure proper field drainage.",
      leaf_curl: "Use insecticide for whitefly control. Remove affected plants. Plant resistant varieties.",
      bollworm: "Apply recommended insecticides. Monitor pest population regularly. Use pheromone traps.",
      red_rot: "Remove and destroy infected plants. Use disease-free setts. Apply Trichoderma bioagent.",
      termites: "Apply chlorpyriphos in soil. Use neem cake. Maintain proper field sanitation.",
      whip_smut: "Use fungicide seed treatment. Remove infected whips. Practice crop rotation.",
      heavy_rain: "Ensure proper drainage. Avoid irrigation. Harvest mature crops if possible.",
      high_temperature: "Increase irrigation frequency. Apply mulch to retain moisture. Provide shade if possible.",
      low_temperature: "Cover young plants. Avoid pruning. Use anti-transpirants.",
      high_wind: "Provide windbreaks. Stake tall plants. Delay fertilizer application.",
      thunderstorm: "Stay indoors. Secure equipment. Avoid working in fields.",
      dust_storm: "Cover crops with nets. Delay irrigation. Apply water to settle dust.",
      heat: "Increase water supply. Apply shade nets. Avoid midday fieldwork.",
      cold: "Use row covers. Apply potassium fertilizer. Delay planting.",
      default: "Monitor field conditions regularly. Consult local agricultural extension for specific advice."
    };
    
    const key = alertType?.toLowerCase().replace(/\s/g, '_');
    return recommendations[key] || recommendations.default;
  };

  // Get alert icon based on type
  const getAlertIcon = (alertType, severity) => {
    const type = alertType?.toLowerCase() || '';
    
    if (type.includes('rain') || type.includes('flood')) return <FaTint className="text-blue-500 text-2xl" />;
    if (type.includes('heat') || type.includes('temperature')) return <FaTemperatureHigh className="text-red-500 text-2xl" />;
    if (type.includes('wind') || type.includes('storm')) return <FaWind className="text-gray-500 text-2xl" />;
    if (type.includes('blight') || type.includes('rot')) return <FaLeaf className="text-green-600 text-2xl" />;
    if (type.includes('worm') || type.includes('termite')) return <FaBug className="text-yellow-600 text-2xl" />;
    if (severity === 'critical') return <FaExclamationTriangle className="text-red-600 text-2xl" />;
    if (severity === 'warning') return <FaExclamationTriangle className="text-yellow-600 text-2xl" />;
    return <FaInfoCircle className="text-blue-500 text-2xl" />;
  };

  // Helper function to get notification title based on alert type
  const getNotificationTitle = (notification) => {
    if (notification.alert_type === "heavy_rain") return "⚠️ Heavy Rain Alert";
    if (notification.alert_type === "high_temperature") return "🌡️ High Temperature Alert";
    if (notification.alert_type === "low_temperature") return "❄️ Low Temperature Alert";
    if (notification.alert_type === "high_wind") return "💨 High Wind Alert";
    if (notification.alert_type === "disease_risk") return "🦠 Disease Risk Alert";
    if (notification.alert_type === "bacterial_blight") return "🌿 Bacterial Blight Alert";
    if (notification.alert_type === "leaf_curl") return "🍃 Leaf Curl Alert";
    if (notification.alert_type === "bollworm") return "🐛 Bollworm Risk Alert";
    if (notification.alert_type === "red_rot") return "🔴 Red Rot Alert";
    if (notification.alert_type === "termites") return "🐜 Termites Alert";
    if (notification.alert_type === "whip_smut") return "🌾 Whip Smut Alert";
    if (notification.alert_type === "thunderstorm") return "⛈️ Thunderstorm Alert";
    if (notification.alert_type === "dust_storm") return "🌪️ Dust Storm Alert";
    if (notification.alert_type === "heat") return "🔥 Heat Alert";
    if (notification.alert_type === "cold") return "❄️ Cold Wave Alert";
    return `Weather Alert: ${notification.alert_type?.replace(/_/g, ' ') || "Weather Update"}`;
  };

  // Helper function to get notification message
  const getNotificationMessage = (notification) => {
    if (notification.message) return notification.message;
    
    const messages = {
      bacterial_blight: "High humidity conditions detected. Risk of Bacterial Blight disease in cotton.",
      leaf_curl: "Moderate humidity with optimal temperature. Monitor for Leaf Curl virus.",
      bollworm: "Dry conditions prevailing. Increased risk of Bollworm infestation.",
      red_rot: "Critical humidity levels. High risk of Red Rot disease in sugarcane.",
      termites: "Prolonged dry conditions. Termite activity likely to increase.",
      whip_smut: "Dry and windy conditions favorable for Whip Smut development.",
      heavy_rain: "Heavy rainfall expected. Take necessary precautions.",
      thunderstorm: "Thunderstorm activity forecasted. Exercise caution.",
      dust_storm: "Dust storm conditions expected. Protect crops and equipment.",
      heat: "Extreme heat conditions. Implement heat stress management.",
      cold: "Cold wave conditions. Protect sensitive crops."
    };
    
    return messages[notification.alert_type] || 
           `${notification.alert_type?.replace(/_/g, ' ')} alert for ${notification.fieldname || "your field"} on ${notification.alert_date}`;
  };

  // Helper function to format notification time
  const formatNotificationTime = (timestamp) => {
    if (!timestamp) return "Just now";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  // Helper function to get severity icon for Swal
  const getSeverityIcon = (severity) => {
    if (severity === "critical") return "error";
    if (severity === "warning") return "warning";
    return "info";
  };

  // Helper function to get severity color for UI
  const getSeverityColor = (severity) => {
    if (severity === "critical") return "bg-red-100 text-red-800 border-red-200";
    if (severity === "warning") return "bg-yellow-100 text-yellow-800 border-yellow-200";
    return "bg-blue-100 text-blue-800 border-blue-200";
  };

  // Helper function to get severity badge color
  const getSeverityBadgeColor = (severity) => {
    if (severity === "critical") return "bg-red-500";
    if (severity === "warning") return "bg-yellow-500";
    return "bg-blue-500";
  };

  // Helper function to get severity icon component
  const getSeverityIconComponent = (severity) => {
    if (severity === "critical") return <FaExclamationTriangle className="text-red-500" />;
    if (severity === "warning") return <FaExclamationTriangle className="text-yellow-500" />;
    return <FaInfoCircle className="text-blue-500" />;
  };

  // Beautiful detailed notification popup
  const showDetailedNotificationPopup = (notification) => {
    const severityColor = getSeverityBadgeColor(notification.severity);
    const severityIcon = getSeverityIcon(notification.severity);
    
    Swal.fire({
      title: notification.title,
      html: `
        <div class="text-left" style="font-family: system-ui, -apple-system, sans-serif;">
          <!-- Severity Badge -->
          <div class="flex justify-center mb-4">
            <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold text-white ${severityColor}">
              ${notification.severity ? notification.severity.toUpperCase() : 'INFO'}
            </span>
          </div>
          
          <!-- Main Message -->
          <div class="bg-gray-50 rounded-lg p-4 mb-4">
            <p class="text-gray-700 text-sm leading-relaxed">${notification.message}</p>
          </div>
          
          <!-- Field Information -->
          ${notification.fieldname ? `
            <div class="mb-3 flex items-start gap-2">
              <div class="flex-shrink-0 mt-0.5">
                <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
              </div>
              <div>
                <p class="text-xs text-gray-500">Field</p>
                <p class="text-sm font-medium text-gray-800">${notification.fieldname}</p>
              </div>
            </div>
          ` : ''}
          
          <!-- Date Information -->
          ${notification.alert_date ? `
            <div class="mb-3 flex items-start gap-2">
              <div class="flex-shrink-0 mt-0.5">
                <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
              </div>
              <div>
                <p class="text-xs text-gray-500">Alert Date</p>
                <p class="text-sm font-medium text-gray-800">${notification.alert_date}</p>
              </div>
            </div>
          ` : ''}
          
          <!-- Recommendations Section -->
          <div class="mt-4 pt-3 border-t border-gray-200">
            <div class="flex items-center gap-2 mb-2">
              <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <p class="text-sm font-semibold text-gray-800">Recommended Actions</p>
            </div>
            <div class="bg-green-50 rounded-lg p-3">
              <p class="text-xs text-green-800 leading-relaxed">${notification.recommendations || "Monitor field conditions regularly. Consult local agricultural extension for specific advice."}</p>
            </div>
          </div>
        </div>
      `,
      icon: severityIcon,
      showConfirmButton: true,
      confirmButtonText: 'Got it',
      confirmButtonColor: notification.severity === "critical" ? "#d33" : (notification.severity === "warning" ? "#f59e0b" : "#3085d6"),
      showCloseButton: true,
      background: '#ffffff',
      customClass: {
        popup: 'rounded-2xl shadow-2xl',
        title: 'text-xl font-bold text-gray-800 mb-2',
        confirmButton: 'px-6 py-2 rounded-lg font-medium',
        closeButton: 'text-gray-400 hover:text-gray-600'
      }
    });
  };

  const toggleNotifications = () => {
    if (!isActive) return;
    setShowNotifications(!showNotifications);
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if not already read
    if (!notification.read) {
      await markAsRead(notification.id);
    }
    // Show detailed popup
    showDetailedNotificationPopup(notification);
  };

  const markAsRead = async (id) => {
    // Update local state immediately for better UX
    setNotifications(prev => prev.map(notif => 
      notif.id === id ? { ...notif, read: true } : notif
    ));
    setUnreadCount(prev => Math.max(0, prev - 1));
    
    // Call backend API to mark as read
    await markNotificationAsRead(id);
  };

  const markAllAsRead = async () => {
    if (markingAsRead) return;
    
    setMarkingAsRead(true);
    try {
      const user = getAuth().currentUser;
      if (user) {
       
        await markAllNotificationsAsRead(user.email);
      }
      
      // Update local state
      setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
      setUnreadCount(0);
      
      Swal.fire({
        title: "Success",
        text: "All notifications marked as read",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: "top-end",
      });
    } catch (error) {
      console.error("Error marking all as read:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to mark all notifications as read",
        icon: "error",
        timer: 1500,
        showConfirmButton: false,
      });
    } finally {
      setMarkingAsRead(false);
    }
  };

  const closeNotifications = () => {
    setShowNotifications(false);
  };

  // Enhanced field data fetching - MODIFIED to set fieldsLoaded flag
  const getFieldData = async () => {
    try {
      const user = getAuth().currentUser;
      if (user) {
        const db = getFirestore();
        const fieldsCollectionRef = collection(db, `users/${user.uid}/fields`);
        const querySnapshot = await getDocs(fieldsCollectionRef);

        const batch = writeBatch(db);
        const fields = [];
        const initialDates = {};
        const loadingStates = {};

        for (const document of querySnapshot.docs) {
          const fieldData = document.data();

          if (!fieldData.createdAt) {
            const createdAt = Timestamp.fromDate(new Date());
            batch.set(
              doc(db, `users/${user.uid}/fields`, document.id),
              { createdAt },
              { merge: true },
            );
            fieldData.createdAt = createdAt;
          }

          const hasLastFetchedDate =
            fieldData.lastFetchedDate &&
            fieldData.lastFetchedDate !== "No data available";

          if (hasLastFetchedDate) {
            initialDates[fieldData.fieldName] = formatLastFetchedDate(
              fieldData.lastFetchedDate,
            );
            loadingStates[fieldData.fieldName] = false;
          } else {
            loadingStates[fieldData.fieldName] = true;
          }

          fields.push({
            id: document.id,
            ...fieldData,
            sowindate: fieldData.sowingDate || "",
          });
        }

        if (fields.length > 0) {
          await batch.commit();
        }

        setResults(fields);
        localStorage.setItem("FieldLength", fields.length);
        setLastFetchedDates(initialDates);
        setDateLoadingStates(loadingStates);
        
        // Mark fields as loaded
        setFieldsLoaded(true);
        
        // Log the field count
        console.log(`✅ Fields loaded from Firebase: ${fields.length} fields found`);
        
        return fields.length; // Return the number of fields
      } else {
        throw new Error("User must be authenticated to retrieve field data.");
      }
    } catch (error) {
      console.error("Error fetching field data:", error);
      setError(error.message);
      setFieldsLoaded(true); // Still mark as loaded to avoid infinite loading
      return 0;
    }
  };

  console.log("the field is ", result);
  
  useEffect(() => {
    const checkNewField = async () => {
      const isNewField = localStorage.getItem("newField") === "true";
      if (isNewField) {
        localStorage.setItem("newField", "false");
        await getFieldData();
      }
    };

    checkNewField();
    const interval = setInterval(checkNewField, 1000);
    return () => clearInterval(interval);
  }, []);

  // NEW EFFECT: Initialize weather data only after fields are loaded and have length > 0
  useEffect(() => {
    const initializeWeatherData = async () => {
      // Only proceed if fields have been loaded and there are fields
      if (fieldsLoaded && result.length > 0 && !weatherInitialized) {
        console.log(`🌤️ Initializing weather data for ${result.length} fields...`);
        setWeatherInitialized(true);
        
        // Load weather data for the first time
        await loadWeatherData();
        
        // Start the countdown timer
        updateCountdownTimer();
        
        // Set up periodic checks for weather updates
        const weatherCheckInterval = setInterval(() => {
          if (shouldCallWeatherAPI() && fieldsLoaded && result.length > 0) {
            loadWeatherData();
          }
        }, 60 * 60 * 1000); // Check every hour
        
        // Clean up interval on component unmount or when fields become empty
        return () => clearInterval(weatherCheckInterval);
      } else if (fieldsLoaded && result.length === 0) {
        console.log("⚠️ No fields available. Weather notifications will not be initialized.");
        setWeatherInitialized(false);
      }
    };
    
    // Call the initialization function
    const cleanup = initializeWeatherData();
    
    // Return cleanup function
    return () => {
      if (cleanup && typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, [fieldsLoaded, result.length, loadWeatherData, updateCountdownTimer]);

  // Set up timer intervals for countdown
  useEffect(() => {
    // Only set up timer if fields are loaded and have data
    if (fieldsLoaded && result.length > 0) {
      // Set up timer to update countdown every second
      timerIntervalRef.current = setInterval(() => {
        updateCountdownTimer();
      }, 1000);
      
      return () => {
        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      };
    }
  }, [fieldsLoaded, result.length, updateCountdownTimer]);

  // Helper function to format the last fetched date
  const formatLastFetchedDate = (date) => {
    if (!date || date === "No data available") {
      return "No data available";
    }
    return new Date(date).toLocaleDateString("en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Enhanced refresh function to update dates
  const handleRefreshClick = async (coordinates, area, fieldName) => {
    if (loadingFields[fieldName]) return;

    setLoadingFields((prev) => ({ ...prev, [fieldName]: true }));
    setCompletedFields((prev) => ({ ...prev, [fieldName]: false }));

    try {
      const today = new Date();
      const endDate = formatDate(today);
      const startDate =
        lastFetchedDates[fieldName] &&
        lastFetchedDates[fieldName] !== "No data available"
          ? formatDate(new Date(lastFetchedDates[fieldName]))
          : endDate;

      const user = getAuth().currentUser;
      if (!user) {
        throw new Error("User must be authenticated to start batch processing.");
      }

      const formattedCoordinates = coordinates.map((coord) => [
        coord.longitude,
        coord.latitude,
      ]);

      const payload = {
        coordinates: formattedCoordinates,
        start_date: startDate,
        end_date: endDate,
        fieldName: fieldName,
        crop_type: "generic",
        analysis_types: ["NDVI", "NDMI", "MSAVI", "NDRE", "NCC"],
        user_email: user.email,
      };

      const response = await fetch(urls.start_batch, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to start batch processing");
      }

      const data = await response.json();
      await new Promise((resolve) => setTimeout(resolve, 3000));

      const field = result.find((f) => f.fieldName === fieldName);
      if (field) {
        const newDate = await getLastDateFromBackend(fieldName);
        if (newDate) {
          await saveLastDateToFirebase(field.id, fieldName, newDate);
          setLastFetchedDates((prev) => ({
            ...prev,
            [fieldName]: formatLastFetchedDate(newDate),
          }));
        }
      }

      setCompletedFields((prev) => ({ ...prev, [fieldName]: true }));

      Swal.fire({
        title: "Analysis Completed",
        text: `Refetched analysis for ${fieldName.split("-")[0]}.`,
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      setError(error.message);
      setCompletedFields((prev) => ({ ...prev, [fieldName]: false }));
      Swal.fire({
        title: "Error",
        text: error.message,
        icon: "error",
        confirmButtonColor: "#d33",
      });
    } finally {
      setLoadingFields((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleChartClick = (
    coordinates,
    area,
    fieldName,
    sowindate,
    cropName,
  ) => {
    const data = {
      coordinates,
      area,
      fieldName,
      sowindate,
      cropName,
      source: "chartClick",
    };
    localStorage.setItem("data", JSON.stringify(data));
    navigate("/analytics-dashboard");
  };

  const handleCardPress = (
    coordinates,
    area,
    fieldName,
    sowindate,
    cropName,
  ) => {
    const data = {
      coordinates,
      area,
      fieldName,
      sowindate,
      cropName,
      source: "cardPress",
    };
    localStorage.setItem("data", JSON.stringify(data));
    navigate("/analytics-dashboard");
  };

  const handleDeleteClick = async (fieldIds, fieldNames) => {
    const isMultiple = Array.isArray(fieldIds) && fieldIds.length > 1;
    const count = isMultiple ? fieldIds.length : 1;
    const fieldsText = isMultiple
      ? `${count} fields`
      : `the field: ${fieldNames[0]?.split("-")[0]}`;

    const confirmed = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to delete ${fieldsText}. This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Delete it!",
      cancelButtonText: "Cancel",
    });

    if (!confirmed.isConfirmed) return;

    Swal.fire({
      title: "Processing...",
      text: `Deleting ${fieldsText}. Please wait.`,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const user = getAuth().currentUser;
      if (!user)
        throw new Error("User must be authenticated to delete field data.");

      const db = getFirestore();

      try {
        if (isMultiple) {
          const batch = writeBatch(db);
          selectedFields.forEach((id) =>
            batch.delete(doc(db, `users/${user.uid}/fields`, id)),
          );
          await batch.commit();
        } else {
          await deleteDoc(
            doc(
              db,
              `users/${user.uid}/fields`,
              Array.isArray(fieldIds) ? fieldIds[0] : fieldIds,
            ),
          );
        }
      } catch (err) {
        console.warn("⚠️ Firebase field not found or already deleted:", err.message);
      }

      localStorage.setItem("newField", "true");

      try {
        const responseMySql = await fetch(urls.delete_field, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            field_names: fieldNames,
            user_email: user.email,
          }),
        });

        if (!responseMySql.ok) {
          console.warn("⚠️ Field may not exist in MySQL:", responseMySql.status);
        }
      } catch (err) {
        console.warn("⚠️ MySQL deletion skipped:", err.message);
      }

      await Promise.all(
        fieldNames.map(async (fieldName) => {
          try {
            const responseFolder = await fetch(`${urls.delete}/${fieldName}`, {
              method: "DELETE",
              headers: { "Content-Type": "application/json" },
            });
            if (!responseFolder.ok) {
              console.warn(`⚠️ Folder not found for ${fieldName}`);
            }
          } catch (err) {
            console.warn(`⚠️ Folder deletion skipped for ${fieldName}:`, err.message);
          }
        }),
      );

      await Swal.fire({
        title: "Deleted!",
        text: `${fieldsText} have been successfully deleted.`,
        icon: "success",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "OK",
      });

      await getFieldData(); // Refresh field data (this will update fieldsLoaded)
      setSelectedFields([]);
    } catch (error) {
      await Swal.fire({
        title: "Error",
        text: `Unexpected issue while deleting: ${error.message}`,
        icon: "error",
        confirmButtonColor: "#d33",
        confirmButtonText: "OK",
      });
    }
  };

  const getSortedFields = () => {
    const sortedFields = [...result];

    switch (sortBy) {
      case "alphabetical":
        sortedFields.sort((a, b) => {
          const nameA = a.fieldName.toLowerCase();
          const nameB = b.fieldName.toLowerCase();
          return sortOrder === "asc"
            ? nameA.localeCompare(nameB)
            : nameB.localeCompare(nameA);
        });
        break;

      case "date":
        sortedFields.sort((a, b) => {
          const getDateValue = (field) => {
            if (!field.createdAt) return 0;
            let dateValue;
            if (field.createdAt.toDate) {
              dateValue = field.createdAt.toDate().getTime();
            } else if (field.createdAt instanceof Date) {
              dateValue = field.createdAt.getTime();
            } else {
              dateValue = new Date(field.createdAt).getTime();
            }
            return dateValue;
          };

          const dateA = getDateValue(a);
          const dateB = getDateValue(b);
          return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
        });
        break;

      default:
        break;
    }

    return sortedFields;
  };

  const handleSortChange = (newSortBy) => {
    if (newSortBy === sortBy) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(newSortBy);
      setSortOrder(newSortBy === "date" ? "desc" : "asc");
    }
  };

  const handleSelectField = (id) => {
    setSelectedFields((prev) =>
      prev.includes(id)
        ? prev.filter((fieldId) => fieldId !== id)
        : [...prev, id],
    );
  };

  const handleDeleteSelected = () => {
    if (selectedFields.length === 0) {
      Swal.fire({
        title: "No Selection",
        text: "Please select fields to delete.",
        icon: "info",
        timer: 2000,
        showConfirmButton: false,
      });
      return;
    }

    const selectedFieldNames = selectedFields
      .map((fieldId) => {
        const field = result.find((f) => f.id === fieldId);
        return field ? field.fieldName : null;
      })
      .filter(Boolean);

    if (selectedFieldNames.length > 0) {
      handleDeleteClick(selectedFields, selectedFieldNames);
    }
  };

  useEffect(() => {
    const fetchFieldExistence = async () => {
      try {
        const existenceMapping = await compareFields();
        setFieldExistence(existenceMapping);
      } catch (error) {
        console.error("Error comparing fields:", error);
      }
    };

    fetchFieldExistence();
    getFieldData(); // This will set fieldsLoaded when complete
  }, []);

  useEffect(() => {
    const updateFieldLimit = () => {
      const No_Of_Fields = JSON.parse(localStorage.getItem("FieldLength")) || 0;
      const userPlan = localStorage.getItem("userPlan") || "Free";

      const planLimits = {
        Free: 1,
        Diamond: 5,
        Platinum: Infinity,
      };

      const fieldsLeft =
        planLimits[userPlan] === Infinity
          ? "∞"
          : Math.max(planLimits[userPlan] - No_Of_Fields, 0);

      setLimitField(fieldsLeft);
    };

    updateFieldLimit();
    const interval = setInterval(updateFieldLimit, 1000);
    return () => clearInterval(interval);
  }, []);

  // Filter fields based on search query
  const filteredFields = getSortedFields().filter((field) =>
    field.fieldName.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div className="bg-base-100 h-full w-full p-4 rounded-xl border border-gray-200 shadow-sm overflow-y-auto relative">
      {/* Header Section */}
      <div className="mb-6">
        <div className="flex flex-col gap-4 mb-4">
          {/* 1st Row: Field Management Heading */}
          <div>
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <FaMapMarkerAlt className="text-red-500 animate-bounce" />
                {t("Field Management")}
              </h2>
              
              {/* Notification Bell */}
              <button
                ref={bellButtonRef}
                className="notification-bell flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed relative transition-all duration-300"
                disabled={!isActive}
                onClick={toggleNotifications}
              >
                <FaBell className={`w-5 h-5 ${unreadCount > 0 ? 'text-green-600' : 'text-gray-600'}`} />
                
                {/* Notification Badge with count */}
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1 animate-pulse">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-1 mb-2">
              {`${limitField === "∞" ? t("Unlimited fields available") : `${limitField} ${t("fields remaining")}`}`}
            </p>
            {/* Mobile timer display - only show if we have data */}
            {timeUntilNextUpdate && result.length > 0 && fieldsLoaded && (
              <div className="md:hidden flex items-center gap-2 text-xs text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full  w-fit">
                <FaClock className="text-green-500" />
                <span>Next update: </span>
                <span className="font-mono font-semibold text-gray-700">
                  {timeUntilNextUpdate.hours}h {timeUntilNextUpdate.minutes}m
                </span>
              </div>
            )}
            {/* Desktop timer display - only show if we have data */}
            {timeUntilNextUpdate && result.length > 0 && fieldsLoaded && (
              <div className="hidden md:flex items-center gap-2 text-xs text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full">
                <FaClock className="text-green-500" />
                <span>The weather next update in: </span>
                <span className="font-mono font-semibold text-gray-700">
                  {timeUntilNextUpdate.hours}h {timeUntilNextUpdate.minutes}m {timeUntilNextUpdate.seconds}s
                </span>
              </div>
            )}
          </div>

          {/* 2nd Row: Sort Buttons and Delete */}
          <div className="flex md:flex-col 2xl:flex-row flex-row gap-3 items-start 2xl:items-center justify-between">
            {/* Sort Controls */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
              <button
                onClick={() => handleSortChange("alphabetical")}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  sortBy === "alphabetical"
                    ? "bg-white text-green-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                {sortBy === "alphabetical" && sortOrder === "asc" ? (
                  <FaSortAlphaDown className="text-sm" />
                ) : (
                  <FaSortAlphaUp className="text-sm" />
                )}
                <span className="whitespace-nowrap">{t("Name")}</span>
              </button>

              <button
                onClick={() => handleSortChange("date")}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  sortBy === "date"
                    ? "bg-white text-green-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                {sortBy === "date" && sortOrder === "desc" ? (
                  <FaSortAmountDown className="text-sm" />
                ) : (
                  <FaSortAmountUp className="text-sm" />
                )}
                <span className="whitespace-nowrap">{t("Date")}</span>
              </button>
            </div>

            {/* Delete Selected Button */}
            {selectedFields.length > 0 && (
              <button
                onClick={handleDeleteSelected}
                className="flex items-center gap-2 px-3 md:py-1.5 py-1 2xl:py-1 bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors text-sm font-medium border border-red-200 whitespace-nowrap mt-2 2xl:mt-0"
              >
                <FaTrash className="text-sm" />
                <span>
                  {t("Delete")} ({selectedFields.length})
                </span>
              </button>
            )}
          </div>
        </div>

        {/* 3rd Row: Search and Sort Indicator */}
        <div className="flex flex-col 2xl:flex-row gap-3 items-start 2xl:items-center justify-between">
          {/* Search Bar */}
          <div className="relative w-full sm:w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder={t("Search fields...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-1 transition-colors text-sm bg-white"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            )}
          </div>

          {/* Sort Indicator */}
          <div className="hidden 2xl:flex items-center gap-2 text-xs text-gray-500 bg-white px-3 py-2 rounded-md w-auto justify-start">
            <FaInfoCircle className="text-green-400" />
            <span className="whitespace-nowrap">
              {t("Sorted by")}:{" "}
              <span className="font-medium text-green-600">
                {sortBy === "alphabetical"
                  ? `${t("Name")} (${sortOrder === "asc" ? "A-Z" : "Z-A"})`
                  : `${t("Date")} (${sortOrder === "desc" ? t("Latest First") : t("Oldest First")})`}
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Notification Dropdown Panel */}
      {showNotifications && (
        <div 
          ref={notificationPanelRef}
          className="absolute right-2  max-w-80 md:max-w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 transition-all duration-300 ease-in-out"
          style={{ top: "80px" }}
        >
          <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-green-50 to-white rounded-t-lg">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2 ">
              <FaBell className="text-green-500" />
              Weather Alerts
              {unreadCount > 0 && (
                <div className="text-xs text-center bg-red-500 text-white px-2 mr-1 py-1 rounded-full">
                  {unreadCount} new
                </div>
              )}
            </h3>
            <div className="flex ">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  disabled={markingAsRead}
                  className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 transition-colors"
                >
                  {markingAsRead ? <FaSpinner className="animate-spin" /> : <FaCheck />}
                  Mark all read
                </button>
              )}
              <button
                onClick={closeNotifications}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes />
              </button>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <FaBell className="mx-auto text-3xl text-gray-300 mb-2" />
                <p className="font-medium">No weather alerts</p>
                <p className="text-xs mt-1">Weather alerts will appear here when field is available</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-all duration-200 ${
                    !notification.read ? "bg-gradient-to-r from-blue-50 to-white" : ""
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getAlertIcon(notification.alert_type, notification.severity)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start mb-1">
                        <p className={`text-sm font-medium ${!notification.read ? "text-gray-900" : "text-gray-600"}`}>
                          {notification.title}
                        </p>
                        <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                          {notification.time}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                        {notification.message}
                      </p>
                      {notification.fieldname && (
                        <p className="text-xs text-gray-400 flex items-center gap-1">
                          <FaMapMarkerAlt className="text-xs" />
                          {notification.fieldname}
                        </p>
                      )}
                      {!notification.read && (
                        <div className="mt-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            New
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <FaExclamationTriangle />
          {error}
        </div>
      )}

      {/* Search Results Info */}
      {searchQuery && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700 text-sm">
          <FaInfoCircle />
          <span>
            {filteredFields.length === 0
              ? t("No fields found matching your search")
              : t("Showing") +
                ` ${filteredFields.length} ` +
                (filteredFields.length === 1 ? t("field") : t("fields")) +
                t(" matching") +
                ' "' +
                searchQuery +
                '"'}
          </span>
        </div>
      )}

      {/* Weather Loading Indicator */}
      {weatherLoading && result.length > 0 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-blue-700 text-sm">
          <FaSpinner className="animate-spin" />
          <span>Updating weather data and alerts...</span>
        </div>
      )}

      {/* Fields Grid */}
      {filteredFields.length > 0 ? (
        <div className="space-y-3">
          {filteredFields.map((field) => {
            const lastFetchedDate = lastFetchedDates[field.fieldName];
            const isDateLoading = dateLoadingStates[field.fieldName];
            const fieldExists = fieldExistence[field.fieldName];
            const createdDate = field.createdAt
              ? (() => {
                  const date = field.createdAt?.toDate
                    ? field.createdAt.toDate()
                    : new Date(field.createdAt);
                  return date.toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  });
                })()
              : "Unknown";

            return (
              <div
                key={field.id}
                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden"
                onClick={() =>
                  handleCardPress(
                    field.coordinates,
                    field.area,
                    field.fieldName,
                    field.sowindate,
                    field.cropName,
                  )
                }
              >
                <div className="p-4">
                  {/* Field Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <input
                        type="checkbox"
                        className="checkbox checkbox-green-400 checkbox-sm"
                        checked={selectedFields.includes(field.id)}
                        onChange={() => handleSelectField(field.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <h3 className="font-semibold text-gray-800 truncate capitalize">
                        {field.fieldName.split("-")[0]}
                      </h3>
                      {field.area && (
                        <span className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded-full whitespace-nowrap">
                          {field.area} acres
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Field Metadata */}
                  <div className="grid grid-cols-2 gap-4 text-xs text-gray-600 mb-3">
                    <div className="flex items-center gap-1">
                      <FaCalendarAlt className="text-gray-400" />
                      <span>
                        {t("Created")}: {createdDate}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <FaChartLine className="text-gray-400" />
                      <span>
                        {isDateLoading ? (
                          <div className="inline-flex items-center gap-1">
                            <FaSpinner className="animate-spin" />
                            {t("loading...")}
                          </div>
                        ) : lastFetchedDate ? (
                          lastFetchedDate
                        ) : (
                          "No data available"
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      {/* Analyze Button */}
                      <button
                        onClick={(e) => {
                          handleChartClick(
                            field.coordinates,
                            field.area,
                            field.fieldName,
                            field.sowindate,
                            field.cropName,
                          );
                          e.stopPropagation();
                        }}
                        className="btn btn-sm bg-white text-green-600 border-green-200 hover:bg-green-100 hover:border-green-300 transition-colors flex items-center gap-2"
                        title={t("Analyze field")}
                      >
                        <img
                          src={require("../../assets/increasingBlack.png")}
                          alt={t("Analyze")}
                          className="w-4 h-4"
                        />
                        <span className="hidden sm:inline">{t("Analyze")}</span>
                      </button>

                      {/* Refetch Analysis Button */}
                      {lastFetchedDate === "No data available" ? (
                        <button
                          className="btn btn-sm bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed flex items-center gap-2"
                          title={t("Field not analyzed yet")}
                          disabled
                        >
                          <FaSpinner />
                          <span className="hidden xl:hidden md:hidden 2xl:block sm:inline">
                            {t("Not Analyzed")}
                          </span>
                        </button>
                      ) : (
                        <div className="relative">
                          {loadingFields[field.fieldName] && (
                            <div className="absolute inset-0 bg-green-100 rounded-md animate-pulse opacity-70"></div>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRefreshClick(
                                field.coordinates,
                                field.area,
                                field.fieldName,
                              );
                            }}
                            className="btn btn-sm bg-green-50 text-green-600 border-green-200 hover:bg-green-100 hover:border-green-300 transition-colors flex items-center gap-2 relative z-10"
                            title={t("Refetch analysis")}
                            disabled={
                              loadingFields[field.fieldName] || isDateLoading
                            }
                          >
                            {loadingFields[field.fieldName] ? (
                              <>
                                <FaSpinner className="animate-spin" />
                                <span className="hidden xl:hidden md:hidden 2xl:block sm:inline">
                                  {t("Refetching")}
                                </span>
                              </>
                            ) : completedFields[field.fieldName] ? (
                              <>
                                <FaCheckCircle className="text-green-500" />
                                <span className="hidden xl:hidden md:hidden 2xl:block sm:inline">
                                  {t("Refetched")}
                                </span>
                              </>
                            ) : (
                              <>
                                <FaSpinner />
                                <span className="hidden xl:hidden md:hidden 2xl:block sm:inline">
                                  {t("Refetch")}
                                </span>
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Delete Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick([field.id], [field.fieldName]);
                      }}
                      className="btn btn-sm bg-red-50 text-red-600 border-red-200 hover:bg-red-100 hover:border-red-300 transition-colors flex items-center gap-2"
                      title={t("Delete field")}
                    >
                      <FaTrash className="text-sm" />
                      <span className="hidden xl:hidden md:hidden 2xl:block sm:inline">
                        {t("Delete")}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Empty State */
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaMapMarkerAlt className="text-gray-400 text-2xl" />
          </div>
          <h3 className="text-lg font-medium text-gray-600 mb-2">
            {searchQuery ? t("No Fields Found") : t("No Fields Added")}
          </h3>
          <p className="text-gray-500 text-sm max-w-sm mx-auto">
            {searchQuery
              ? t("Try adjusting your search terms to find your fields.")
              : t(
                  "Get started by adding your first field to begin analysis and monitoring.",
                )}
          </p>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
            >
              {t("Clear Search")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

FieldData.propTypes = {
  id: PropTypes.string.isRequired,
  coordinates: PropTypes.array.isRequired,
  area: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
};

export default FieldData;